import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { MatchAnnounceGallery } from '../components/MatchAnnounceGallery';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  RefreshCw, Moon, PlayCircle, Send, Lock, Crown, Target, Clock, Activity,
  TrendingUp, TrendingDown, Zap, AlertCircle, ChevronRight, Flame, Shield, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function minutesUntil(iso) {
  try { return Math.round((new Date(iso).getTime() - Date.now()) / 60000); }
  catch { return null; }
}

function countdown(mins) {
  if (mins == null) return '—';
  if (mins < 0) {
    const ago = Math.abs(mins);
    if (ago < 60) return `hace ${ago}m`;
    return `hace ${Math.floor(ago/60)}h${ago%60}m`;
  }
  if (mins < 60) return `en ${mins}m`;
  const h = Math.floor(mins/60);
  if (h < 24) return `en ${h}h${mins%60}m`;
  return `en ${Math.floor(h/24)}d${h%24}h`;
}

function KPI({ label, value, sub, icon: Icon, tone = 'default' }) {
  const toneMap = {
    default: 'text-zinc-200', good: 'text-emerald-400', bad: 'text-red-400',
    warn: 'text-amber-300', info: 'text-sky-300',
  };
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3" data-testid={`ctrl-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-zinc-500 text-[10px] uppercase tracking-wide font-semibold">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-zinc-600" />}
      </div>
      <div className={`text-2xl font-black ${toneMap[tone]}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function PickRow({ pick, tier = 'free' }) {
  const mins = minutesUntil(pick.match_date);
  const tierColors = {
    stake10: 'border-amber-700/40 bg-amber-950/20',
    vip: 'border-violet-700/40 bg-violet-950/10',
    free: 'border-zinc-800 bg-zinc-900/30',
  };
  const tierIcon = {
    stake10: <Lock className="w-3 h-3 text-amber-300" />,
    vip: <Crown className="w-3 h-3 text-violet-300" />,
    free: <Target className="w-3 h-3 text-sky-300" />,
  };
  return (
    <div className={`flex items-center gap-2 rounded-md border p-2 text-xs ${tierColors[tier]}`}>
      {tierIcon[tier]}
      <div className="flex-1 min-w-0">
        <div className="text-white font-semibold truncate">
          {pick.home_team} <span className="text-zinc-500">vs</span> {pick.away_team}
        </div>
        <div className="text-zinc-400 truncate">{pick.prediction} @{pick.odds}</div>
      </div>
      <div className="text-right">
        <div className="text-emerald-400 font-bold">{pick.real_probability}%</div>
        <div className="text-zinc-500">{countdown(mins)}</div>
      </div>
    </div>
  );
}

export default function AdminControl() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sweepRunning, setSweepRunning] = useState(false);
  const [sendingTelegram, setSendingTelegram] = useState(false);
  const [, setTick] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/control/snapshot`, { headers: authHeader() });
      if (!res.ok) throw new Error('snapshot failed');
      const d = await res.json();
      setData(d);
    } catch {
      toast.error('Error cargando snapshot del panel');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const poll = setInterval(refresh, 30000);
    const tickInt = setInterval(() => setTick((v) => v + 1), 30000);
    return () => { clearInterval(poll); clearInterval(tickInt); };
  }, [refresh]);

  const triggerSweep = async () => {
    setSweepRunning(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/cron/nightly-sweep`, { method: 'POST', headers: authHeader() });
      if (!res.ok) throw new Error();
      toast.success('Barrido lanzado en background · refresca en 60s');
      setTimeout(refresh, 65000);
    } catch {
      toast.error('No se pudo lanzar');
    } finally {
      setSweepRunning(false);
    }
  };

  const triggerTelegram = async () => {
    setSendingTelegram(true);
    try {
      const res = await fetch(`${API_URL}/api/telegram/send-picks`, { method: 'POST', headers: authHeader() });
      if (!res.ok) throw new Error();
      const d = await res.json();
      const r = d.results || {};
      toast.success(`Cola Telegram: 🔒${r.stake10_queued || 0} · 👑${r.vip_queued || 0} · 🎯${r.free_queued || 0}`);
      await refresh();
    } catch {
      toast.error('No se pudo programar Telegram');
    } finally {
      setSendingTelegram(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0b0f]">
        <Navbar />
        <div className="flex items-center justify-center h-96 text-zinc-400">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Cargando panel de control...
        </div>
      </div>
    );
  }

  const d = data || {};
  const sweep = d.last_sweep;
  const picks = d.picks || { counts: {} };
  const queue = d.telegram_queue || {};
  const st10 = d.stake10_status || {};
  const btts = d.btts_calibration || {};
  const today = d.today_performance || {};

  const sweepOk = sweep?.success;
  const sweepAgo = sweep?.timestamp ? Math.round((Date.now() - new Date(sweep.timestamp).getTime()) / 60000) : null;
  const nextPubMins = queue.next_publication ? minutesUntil(queue.next_publication.scheduled_for) : null;

  return (
    <div className="min-h-screen bg-[#0a0b0f]">
      <Navbar />
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-sky-900/40">
                <Activity className="w-7 h-7 text-white" data-testid="control-activity-icon" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white">Centro de Control</h1>
                <p className="text-sm text-zinc-400">Todo lo que importa · un vistazo · fase <span className="text-sky-300">{d.phase}</span></p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              data-testid="control-refresh-btn"
              className="border-zinc-700 hover:bg-zinc-800"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
            </Button>
            <Button
              onClick={triggerSweep}
              disabled={sweepRunning}
              data-testid="control-sweep-btn"
              className="bg-sky-600 hover:bg-sky-500 text-white"
            >
              <Moon className="w-4 h-4 mr-1" /> {sweepRunning ? 'Lanzando...' : 'Barrido'}
            </Button>
            <Button
              onClick={triggerTelegram}
              disabled={sendingTelegram}
              data-testid="control-telegram-btn"
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              <Send className="w-4 h-4 mr-1" /> {sendingTelegram ? '...' : 'Programar TG'}
            </Button>
          </div>
        </div>

        {/* Top KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-6">
          <KPI label="Balance S10" value={`${st10.balance >= 0 ? '+' : ''}${(st10.balance || 0).toFixed(1)}u`} sub={`${st10.total || 0} picks · ${st10.win_rate || 0}%`} icon={Target} tone={st10.balance >= 0 ? 'good' : 'bad'} />
          <KPI label="Racha" value={st10.streak_count ? `${st10.streak_count}${st10.streak_type === 'won' ? 'W' : 'L'}` : '—'} sub={st10.in_recovery ? '⚠️ Recovery' : 'Normal'} icon={Flame} tone={st10.streak_type === 'won' ? 'good' : st10.streak_type === 'lost' ? 'bad' : 'default'} />
          <KPI label="Hoy" value={`${today.wins || 0}W-${today.losses || 0}L`} sub={`${today.profit_u >= 0 ? '+' : ''}${today.profit_u || 0}u`} icon={BarChart3} tone={today.profit_u > 0 ? 'good' : today.profit_u < 0 ? 'bad' : 'default'} />
          <KPI label="Picks pending" value={picks.counts.total || 0} sub={`🔒${picks.counts.stake10 || 0} · 👑${picks.counts.vip || 0} · 🎯${picks.counts.free || 0}`} icon={Zap} tone="info" />
          <KPI label="Cola TG" value={queue.total_pending || 0} sub={nextPubMins != null ? `próx ${countdown(nextPubMins)}` : 'vacía'} icon={Send} tone={queue.total_pending ? 'info' : 'default'} />
          <KPI label="BTTS xG thr" value={btts.xg_threshold?.toFixed(1) ?? '—'} sub={`+${btts.yes_boost?.toFixed(2) ?? '1.00'} / ×${btts.no_penalty?.toFixed(2) ?? '1.00'}`} icon={TrendingUp} tone="default" />
        </div>

        {/* Main grid */}
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          {/* Sweep card */}
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-white text-base">
                <Moon className="w-4 h-4 text-sky-300" /> Último barrido
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sweep ? (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Cuándo</span>
                    <span className="text-zinc-200">{sweepAgo != null ? `hace ${sweepAgo}m` : '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Estado</span>
                    <Badge className={sweepOk ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40' : 'bg-red-900/40 text-red-300 border-red-700/40'}>
                      {sweepOk ? '✓ OK' : '✗ Error'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Eventos / Wiped</span>
                    <span className="text-zinc-200">{sweep.events_fetched || 0} · {sweep.wiped_picks || 0}</span>
                  </div>
                  <div className="text-xs text-zinc-400 pt-2 border-t border-zinc-800">
                    {sweep.result}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-zinc-500 py-2">
                  Sin histórico. Cron automático 00:15 UTC diario.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Telegram queue */}
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-white text-base">
                <Send className="w-4 h-4 text-emerald-300" /> Cola Telegram
              </CardTitle>
            </CardHeader>
            <CardContent>
              {queue.total_pending > 0 ? (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Total pendiente</span>
                    <span className="text-emerald-300 font-bold">{queue.total_pending}</span>
                  </div>
                  {queue.next_publication && (
                    <div className="border-t border-zinc-800 pt-2 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-500">Próxima</span>
                        <span className="text-sky-300 font-bold text-xs">{countdown(nextPubMins)}</span>
                      </div>
                      <div className="text-xs text-zinc-400">
                        <span className="text-zinc-500">Tipo:</span> {queue.next_publication.kind}
                        {' · '}
                        <span className="text-zinc-500">Canal:</span> {queue.next_publication.channel_target || queue.next_publication.channel}
                      </div>
                    </div>
                  )}
                  <div className="border-t border-zinc-800 pt-2">
                    <div className="text-xs text-zinc-500 mb-1">Por tipo</div>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(queue.by_kind || {}).map(([k, v]) => (
                        <Badge key={k} className="bg-zinc-800 text-zinc-300 border-zinc-700 text-[10px]">
                          {k} · {v}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-zinc-500 py-2">
                  Cola vacía. Usa <b>Programar TG</b> para encolar las picks pendientes.
                </div>
              )}
            </CardContent>
          </Card>

          {/* BTTS calibration summary */}
          <Card className="bg-zinc-900/60 border-zinc-800">
            <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2 text-white text-base">
                <TrendingUp className="w-4 h-4 text-emerald-300" /> BTTS Calibración
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">xG threshold</span>
                  <span className="text-zinc-200 font-mono">≥{btts.xg_threshold?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">Yes boost</span>
                  <span className="text-emerald-300 font-mono">×{btts.yes_boost?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">No penalty</span>
                  <span className="text-red-300 font-mono">×{btts.no_penalty?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between border-t border-zinc-800 pt-2">
                  <span className="text-zinc-500">Auto-mode</span>
                  <Badge className={btts.auto_mode ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}>
                    {btts.auto_mode ? 'ON' : 'OFF'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Match Announce Gallery (24h-before reels preview) */}
        <MatchAnnounceGallery />

        {/* Disabled markets row */}
        {(d.disabled_markets || []).length > 0 && (
          <Card className="bg-zinc-900/60 border-red-700/30 mb-4">            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-red-300 text-base">
                <AlertCircle className="w-4 h-4" /> Mercados desactivados ({d.disabled_markets.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2" data-testid="control-disabled-markets">
                {d.disabled_markets.map((m) => (
                  <Badge
                    key={m.market_key}
                    className={m.auto ? 'bg-red-950/40 text-red-300 border-red-700/40' : 'bg-zinc-800 text-zinc-300 border-zinc-700'}
                    title={m.reason}
                  >
                    {m.auto ? '🤖' : '🔒'} {m.market_key}
                    {m.win_rate != null && <span className="ml-1 opacity-70">· {m.win_rate}%</span>}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Picks by tier */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-zinc-900/60 border-amber-700/30">
            <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2 text-amber-300 text-base">
                <Lock className="w-4 h-4" /> STAKE 10 <span className="text-xs text-zinc-500">({picks.counts.stake10 || 0})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5" data-testid="control-stake10-list">
              {(picks.stake10_today || []).length === 0 && (
                <div className="text-xs text-zinc-500 py-3 text-center">Sin STAKE 10 activos</div>
              )}
              {(picks.stake10_today || []).map((p) => <PickRow key={p.id} pick={p} tier="stake10" />)}
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/60 border-violet-700/30">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-violet-300 text-base">
                <Crown className="w-4 h-4" /> VIP <span className="text-xs text-zinc-500">({picks.counts.vip || 0})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5" data-testid="control-vip-list">
              {(picks.vip_pending || []).length === 0 && (
                <div className="text-xs text-zinc-500 py-3 text-center">Sin VIP pendientes</div>
              )}
              {(picks.vip_pending || []).map((p) => <PickRow key={p.id} pick={p} tier="vip" />)}
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/60 border-sky-700/30">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sky-300 text-base">
                <Target className="w-4 h-4" /> FREE <span className="text-xs text-zinc-500">({picks.counts.free || 0})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5" data-testid="control-free-list">
              {(picks.free_pending || []).length === 0 && (
                <div className="text-xs text-zinc-500 py-3 text-center">Sin FREE pendientes</div>
              )}
              {(picks.free_pending || []).map((p) => <PickRow key={p.id} pick={p} tier="free" />)}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
